import { useMemo, useState } from 'react';
import { addDays } from 'date-fns';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';

import { Box } from '@mui/material';
import { useTheme, makeStyles } from '@mui/styles';

import { DialogActionButtons } from '../../dialog';
import locales from '../../../../constants/locales';
import { DateType } from '../../../../constants/helpers/dates';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

const useStyles = makeStyles(theme => ({
  rangePicker: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column-reverse',
      '& > .rdrCalendarWrapper': {
        alignItems: 'center',
        '& > .rdrDateDisplayWrapper': {
          width: '100%'
        }
      }
    },
    '& > .rdrDefinedRangesWrapper': {
      display: ({ nowSelected }: { nowSelected: boolean }) => (nowSelected ? 'none' : 'initial'),
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    }
  }
}));

interface Props {
  nowSelected: boolean;
  from: DateType;
  to: DateType;
  handleCloseDialog: () => void;
  handleSubmit: ({ startDate, endDate }: { startDate: Date; endDate: Date }) => void;
  openPicker: boolean;
}
const RangePicker = ({ nowSelected, from, to, handleCloseDialog, handleSubmit }: Props) => {
  const theme = useTheme();
  const initialState = useMemo(() => {
    const currentDate = new Date();
    const endDateWithoutNowSelected = to ? new Date(to) : currentDate;

    return {
      startDate: from ? new Date(from) : currentDate,
      endDate: nowSelected ? currentDate : endDateWithoutNowSelected
    };
  }, [from, nowSelected, to]);

  const [rangeValue, setRangeValue] = useState(initialState);
  const classes = useStyles({ nowSelected });
  const handleSelect = ({ range }: RangeKeyDict) => {
    const currentDate = new Date();
    const endDateWithoutNowSelected = range.endDate || currentDate;

    setRangeValue({
      startDate: range.startDate || currentDate,
      endDate: nowSelected ? currentDate : endDateWithoutNowSelected
    });
  };

  const startDate = new Date(2015, 1, 1);
  return (
    <>
      <Box>
        <DateRangePicker
          {...(nowSelected && {
            focusedRange: [0, 0],
            staticRanges: [],
            inputRanges: [],
            maxDate: rangeValue.endDate || new Date()
          })}
          fixedHeight={true}
          minDate={startDate}
          maxDate={addDays(new Date(), 365)}
          retainEndDateOnFirstSelection={true}
          ranges={[
            {
              startDate: rangeValue.startDate || undefined,
              endDate: rangeValue.endDate || undefined,
              key: 'range',
              color: theme.palette.primary.main
            }
          ]}
          className={classes.rangePicker}
          onChange={handleSelect}
        />
      </Box>
      <DialogActionButtons
        danger={false}
        actions={{
          primary: {
            type: 'button' as const,
            handler: () => handleSubmit(rangeValue),
            text: locales.button.title.apply
          },
          secondary: {
            type: 'button' as const,
            handler: handleCloseDialog,
            text: locales.button.title.cancel
          }
        }}
      />
    </>
  );
};

export default RangePicker;
